import { useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import * as Crypto from 'expo-crypto';
import SheetModal from '@/components/General/SheetModal';
import Callout from '@/components/General/Callout';
import Unicon from '@/components/General/Unicon';
import Chip from '@/components/General/Chip';
import Input from '@/components/General/inputs/Input';
import InputSection from '@/components/General/inputs/InputSection';
import { Query } from '@/utils/react-query/Queries';
import { Mutation } from '@/utils/react-query/Mutations';
import { parseTimeFrame } from '@/utils/dater';
import {
    buildCloseCarePayload,
    countCloseCancels,
    formatMealDate,
    initialCloseDecisions,
    summarizeCloseResult,
    toggleCloseDecision,
} from '@/utils/followUpCare';
import { getTaskPurposeIcon, getTaskPurposeLabel } from '@/utils/constants/followUp';

/**
 * CloseCareSheet — the explicit care closure review (spec §13.6, C12–C14).
 *
 * Opens with the server's pendingWork preview (never mutates). Every item
 * defaults to KEEP — the spec default is preserve — and the user picks an
 * explicit keep-or-cancel per item. An optional closure note is stored on
 * the episode and written as a PeopleNote. Work that completed between
 * preview and apply comes back in skippedTaskDecisions and is reported,
 * never cancelled (C13). Nothing is auto-completed and nothing is deleted.
 */
const CloseCareSheet = ({ isOpen, onClose, organizationID, careEpisodeID, personID, personName }) => {
    const idempotencyKeyRef = useRef(null);
    const [decisions, setDecisions] = useState({});
    const [closureNote, setClosureNote] = useState('');
    const [result, setResult] = useState(null);

    const { data: preview, isLoading, isError, refetch } = Query.followUp.careClosePreview({
        organizationID,
        careEpisodeID,
        options: { enabled: isOpen && !!organizationID && !!careEpisodeID },
    });

    const pendingWork = preview?.pendingWork || [];

    // Seed decisions when the preview lands; items that appear later default
    // to keep — preserve is the only safe default (§13.6).
    useEffect(() => {
        if (pendingWork.length) {
            setDecisions((prev) => ({ ...initialCloseDecisions(pendingWork), ...prev }));
        }
    }, [pendingWork.length]);

    useEffect(() => {
        if (isOpen) {
            idempotencyKeyRef.current = Crypto.randomUUID();
            setDecisions({});
            setClosureNote('');
            setResult(null);
        } else {
            idempotencyKeyRef.current = null;
        }
    }, [isOpen]);

    const closeMutation = Mutation.followUp.closeCare({
        onSuccess: (data) => setResult(data),
        onError: (error) => {
            if (!error?.expected) {
                // Refetch the preview so decisions never address stale work.
                refetch();
            }
        },
    });

    const cancelCount = countCloseCancels(decisions);

    const handleConfirm = () => {
        if (closeMutation.isPending) return;
        const { payload } = buildCloseCarePayload({
            careEpisodeID,
            pendingWork,
            decisions,
            closureNote,
            idempotencyKey: idempotencyKeyRef.current,
        });
        if (!payload) return;
        closeMutation.mutate({ organizationID, careEpisodeID, personID, payload });
    };

    const summaryText = result ? summarizeCloseResult(result) : null;

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            onCancel={onClose}
            title="Close Care"
            subtitle={personName || undefined}
            stayOpenOnBlur
            snapPoints={[440, 660]}
            cancelText="Cancel"
            confirmText={result ? 'Done' : 'Close Care'}
            onConfirm={result ? onClose : (closeMutation.isPending ? null : handleConfirm)}
            loading={closeMutation.isPending || (isOpen && isLoading)}
        >
            {result ? (
                <View testID="close-care-result" style={styles.resultWrap}>
                    <Callout
                        type="success"
                        title="Care closed"
                        message={`The episode is now closed and kept in history.${summaryText ? ` ${summaryText}.` : ''}`}
                    />
                    {(result.skippedTaskDecisions || []).length > 0 && (
                        <Text style={styles.resultHint}>
                            Some work changed state while you reviewed. Its outcome was kept as recorded.
                        </Text>
                    )}
                </View>
            ) : isLoading ? (
                <View style={styles.loadingWrap}><ActivityIndicator /></View>
            ) : isError ? (
                <Callout
                    type="error"
                    message="Couldn't load the open work on this episode. Close the sheet and try again."
                    testID="close-care-error"
                />
            ) : (
                <ScrollView style={styles.scroll} keyboardShouldPersistTaps="handled">
                    <Callout
                        type="info"
                        message="Closing ends this care episode. History stays. Choose what happens to each open item. Nothing is completed or deleted automatically."
                    />

                    {pendingWork.length > 0 ? (
                        <>
                            <Text style={styles.label}>Open work ({pendingWork.length})</Text>
                            {pendingWork.map((item) => {
                                const decision = decisions[item.taskInstanceID] || 'keep';
                                const cancelling = decision === 'cancel';
                                const assigneeNames = (item.assignees || []).map((a) => a.name).filter(Boolean);
                                return (
                                    <Pressable
                                        key={item.taskInstanceID}
                                        testID={`close-care-item-${item.taskInstanceID}`}
                                        accessibilityRole="button"
                                        accessibilityLabel={`${cancelling ? 'Keep' : 'Cancel'} ${item.title || 'task'}`}
                                        onPress={() => setDecisions((prev) => toggleCloseDecision(prev, item.taskInstanceID))}
                                        style={[styles.workRow, cancelling && styles.workRowCancel]}
                                    >
                                        <View style={styles.workIcon}>
                                            <Unicon name={getTaskPurposeIcon(item.purpose)} size={15} colorVariant="paths" />
                                        </View>
                                        <View style={styles.workBody}>
                                            <Text style={styles.workTitle} numberOfLines={1}>
                                                {item.title || getTaskPurposeLabel(item.purpose)}
                                            </Text>
                                            <Text style={styles.workMeta} numberOfLines={1}>
                                                {[
                                                    item.mealDate ? `Meal on ${formatMealDate(item.mealDate)}` : null,
                                                    item.dueDate ? `Due ${parseTimeFrame(item.dueDate)}` : null,
                                                    assigneeNames.length ? assigneeNames.join(', ') : 'Unassigned',
                                                ].filter(Boolean).join(' · ')}
                                            </Text>
                                        </View>
                                        <Chip
                                            icon={cancelling ? 'close-circle-outline' : 'checkmark-circle-outline'}
                                            colorVariant={cancelling ? 'error' : 'textSecondary'}
                                            testID={`close-care-decision-${item.taskInstanceID}`}
                                        >
                                            {cancelling ? 'Will cancel' : 'Keep open'}
                                        </Chip>
                                    </Pressable>
                                );
                            })}
                            {cancelCount > 0 && (
                                <Text style={styles.cancelHint}>
                                    {`${cancelCount} item${cancelCount === 1 ? '' : 's'} will be cancelled on close.`}
                                </Text>
                            )}
                        </>
                    ) : (
                        <Text style={styles.emptyText}>No open work on this episode.</Text>
                    )}

                    <InputSection>
                        <Input
                            title="Closure note (optional)"
                            placeholder="e.g. Home and recovering, meals no longer needed"
                            value={closureNote}
                            multiline
                            onUpdate={setClosureNote}
                            testID="close-care-note"
                        />
                    </InputSection>
                </ScrollView>
            )}
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    scroll: { flexGrow: 0 },
    loadingWrap: { padding: 32, alignItems: 'center' },
    resultWrap: { gap: 8 },
    resultHint: { fontSize: 12, color: colors.textSecondary, paddingHorizontal: 4 },
    label: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        marginTop: 10,
        marginBottom: 6,
    },
    workRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: 10,
        padding: 10,
        marginBottom: 6,
        borderWidth: 1,
        borderColor: 'transparent',
        boxShadow: shadows.card,
        _web: { cursor: 'pointer' },
    },
    workRowCancel: {
        borderColor: colors.error,
        backgroundColor: colors.errorContainer,
    },
    workIcon: {
        width: 28,
        height: 28,
        borderRadius: 7,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    workBody: { flex: 1, minWidth: 0 },
    workTitle: { fontSize: 13, fontFamily: fonts.bold, color: colors.text },
    workMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 1 },
    cancelHint: { fontSize: 12, color: colors.error, marginTop: 4, paddingHorizontal: 4 },
    emptyText: { fontSize: 13, color: colors.textSecondary, fontStyle: 'italic', marginTop: 8, paddingHorizontal: 4 },
}));

export default CloseCareSheet;
